import { useMemo } from "react";
import { ClientsWorkOverviewColumn } from "@common/types/views/clientsWorkOverviewView";
import { OrganizationAction } from "@common/userOrganizationRoles";
import type { WorkDateField } from "@api/workItem.api";
import { isBusyClosesDemoId } from "@components/demo/busyClosesDemo/busyClosesDemoIds";
import { createColumns } from "@design-system/DataGrid";
import { useCanPerformOrganizationAction } from "@hooks/permissions/organization/useCanPerformOrganizationAction";
import type {
    ClientsWorkOverviewRow,
    ClientsWorkOverviewWorkRow,
} from "../clientsWorkOverviewViewConfig";
import { useConfirmWorkStartChange } from "../editing/useConfirmWorkStartChange";
import { getWorkDateBounds, toWorkDateEdit } from "../editing/workDateEdit";
import {
    getWorkSkipReason,
    hasOpenWorkRole,
    useEditWorkItem,
    type WorkSkipReason,
} from "../editing/workItemEditing";
import { workValue } from "./workListValues";

const { WORK_START_DATE, WORK_DELIVERY_DATE, PREPARER_REVIEWER } =
    ClientsWorkOverviewColumn;

const listEditors = createColumns<ClientsWorkOverviewRow>().edit;

/** The list columns whose cells edit work. */
const WORK_EDIT_COLUMN_IDS: ReadonlySet<string> = new Set([
    WORK_START_DATE,
    WORK_DELIVERY_DATE,
    PREPARER_REVIEWER,
]);

/** The tooltip on an editable column's cell that can't change on its row. */
const WORK_READ_ONLY_REASONS: Record<WorkSkipReason, string> = {
    doneOrCanceled: "Done or canceled work can't be updated",
    noOpenTasks: "No open tasks to reassign",
};

interface WorkCell {
    row: ClientsWorkOverviewRow;
    columnId: string;
}

const getReadOnlySkipReason = ({
    row,
    columnId,
}: WorkCell): WorkSkipReason | null => {
    if (row.rowType !== "work" || !WORK_EDIT_COLUMN_IDS.has(columnId)) {
        return null;
    }
    const reason = getWorkSkipReason(row);
    if (reason) {
        return reason;
    }
    return columnId === PREPARER_REVIEWER && !hasOpenWorkRole(row)
        ? "noOpenTasks"
        : null;
};

/**
 * Why an editable cell can't change on its row: done and canceled work never
 * changes, and the team needs open tasks to reassign.
 */
const getWorkReadOnlyReason = (cell: WorkCell): string | undefined => {
    const reason = getReadOnlySkipReason(cell);
    return reason ? WORK_READ_ONLY_REASONS[reason] : undefined;
};

/** Only work edits: a client with no work isn't a label row, but has nothing to save; busy closes demo work never saves. */
const isWorkCellEditable = (cell: WorkCell) =>
    cell.row.rowType === "work" &&
    !isBusyClosesDemoId(cell.row.closeId) &&
    getWorkReadOnlyReason(cell) === undefined;

/** The table's `listEditing`: only open work edits, as `getWorkReadOnlyReason` explains. */
export const WORK_LIST_EDITING = {
    isCellEditable: isWorkCellEditable,
    getReadOnlyReason: getWorkReadOnlyReason,
};

/** What the list's editors save through. */
export interface WorkListEditing {
    saveWorkDate: (args: {
        row: ClientsWorkOverviewWorkRow;
        field: WorkDateField;
        date: string;
    }) => void;
}

/**
 * A work date's calendar editor: never cleared, bounded so Work start stays
 * on or before Work end. Only work rows reach `commit`.
 */
export const buildWorkDateEditor = ({
    field,
    editing,
}: {
    field: WorkDateField;
    editing: WorkListEditing | null;
}) => {
    const readDate = workValue((work) => work[field]);
    return editing
        ? listEditors.date({
              value: (row) => readDate(row) ?? "",
              commit: ({ row, value }) => {
                  if (row.rowType === "work") {
                      editing.saveWorkDate({ row, field, date: value });
                  }
              },
              placeholder: "Add date",
              isClearable: false,
              minDate: workValue(
                  (work) => getWorkDateBounds(work, field).minDate,
              ),
              maxDate: workValue(
                  (work) => getWorkDateBounds(work, field).maxDate,
              ),
          })
        : undefined;
};

/**
 * The list's editing, or `null` for users who can't update work (it needs
 * the organization settings permission). A new Work start saves once the
 * user confirms it in `confirmationModal`, which the table renders once.
 */
export const useWorkListEditing = () => {
    const canEditWork = useCanPerformOrganizationAction(
        OrganizationAction.MANAGE_ORGANIZATION_SETTINGS,
    );
    const editWorkItem = useEditWorkItem();
    const { saveEdit, confirmationModal } = useConfirmWorkStartChange();
    const editing = useMemo(
        (): WorkListEditing | null =>
            canEditWork
                ? {
                      saveWorkDate: ({ row, field, date }) => {
                          const edit = toWorkDateEdit({ row, field, date });
                          if (edit) {
                              saveEdit({
                                  edit,
                                  save: () => editWorkItem(row, edit),
                              });
                          }
                      },
                  }
                : null,
        [canEditWork, editWorkItem, saveEdit],
    );
    return { editing, confirmationModal };
};
